// @oasis-permission-inherits: /jerboa-editor-session (jerboa:view)
import { createFileRoute } from "@tanstack/react-router";

import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  formatDurationMs,
  formatMb,
  formatTimestamp,
} from "~/modules/jerboa-client-preview/formatters";
import { MemoryHeatmap } from "~/modules/jerboa-client-preview/memory-heatmap";
import { ActionsHeatmap } from "~/modules/jerboa-editor-session/actions-heatmap";
import { SessionInteractionsCard } from "~/modules/jerboa-editor-session/interactions-card";
import { getJerboaEditorSessionQuery } from "~/modules/jerboa-editor-session/queries";
import { JerboaUserEnvCard } from "~/modules/jerboa/components/user-env-card";

export const Route = createFileRoute("/_dash/jerboa-editor-session/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data, isLoading, error } = getJerboaEditorSessionQuery.useQuery({
    pathParams: { id },
  });

  if (error) {
    return (
      <div className="px-6 py-4 text-sm text-destructive">
        Failed to load editor session: {error.message}
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-3 px-6 py-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const session = data.result;

  return (
    <div className="space-y-4 px-6 py-4">
      <div>
        <h1 className="text-xl font-bold">Client Session</h1>
        <p className="font-mono text-xs text-muted-foreground">{session.id}</p>
      </div>

      <JerboaUserEnvCard
        id={session.id}
        captionsProjectId={session.captionsProjectId}
        projectProtoUri={session.projectProtoUri}
        sessionId={session.sessionId}
        userId={session.userId}
        orgId={session.orgId}
        browserInfo={session.browserInfo}
      />

      <Card>
        <CardHeader>
          <CardTitle>Session aggregates</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
            <div>
              <dt className="text-muted-foreground">Created</dt>
              <dd className="font-mono">
                {formatTimestamp(session.createdAt as unknown as string)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Duration</dt>
              <dd>{formatDurationMs(session.sessionDurationMs)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Trigger</dt>
              <dd
                className={
                  session.trigger === "crash"
                    ? "font-mono font-semibold text-destructive"
                    : "font-mono"
                }
              >
                {session.trigger}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Peak reserved</dt>
              <dd>{formatMb(session.peakWasmReservedMb)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Peak live</dt>
              <dd>{formatMb(session.peakWasmLiveMb)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Peak JS heap</dt>
              <dd>{formatMb(session.peakJsHeapMb)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Avg live</dt>
              <dd>{formatMb(session.avgWasmLiveMb)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Slope MB/min</dt>
              <dd className="font-mono">
                {session.liveBaselineSlopeMbPerMin == null
                  ? "-"
                  : session.liveBaselineSlopeMbPerMin.toFixed(2)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Fragmentation</dt>
              <dd className="font-mono">
                {session.fragmentationRatio == null ? "-" : session.fragmentationRatio.toFixed(2)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Total actions</dt>
              <dd>{session.totalActionsApplied}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Undo / redo / hist clear</dt>
              <dd>
                {session.undoCount} / {session.redoCount} / {session.historyClearCount}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <SessionInteractionsCard interactions={session.interactions} />

      <Card>
        <CardHeader>
          <CardTitle>Memory time-series</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {session.wasmReservedBuckets && session.wasmReservedBuckets.length > 0 ? (
            <MemoryHeatmap
              samples={session.wasmReservedBuckets}
              bucketWindowMs={session.bucketWindowMs}
              label="WASM linear memory (reserved)"
              projectStartSecs={null}
            />
          ) : (
            <div className="rounded-md border border-dashed p-4 text-xs text-muted-foreground">
              No reserved-memory samples captured.
            </div>
          )}
          {session.wasmLiveBuckets && session.wasmLiveBuckets.length > 0 ? (
            <MemoryHeatmap
              samples={session.wasmLiveBuckets}
              bucketWindowMs={session.bucketWindowMs}
              label="WASM allocator live bytes"
              projectStartSecs={null}
            />
          ) : null}
          {session.jsHeapBuckets && session.jsHeapBuckets.length > 0 ? (
            <MemoryHeatmap
              samples={session.jsHeapBuckets}
              bucketWindowMs={session.bucketWindowMs}
              label="JS heap"
              projectStartSecs={null}
            />
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Actions time-series</CardTitle>
        </CardHeader>
        <CardContent>
          <ActionsHeatmap
            events={session.events}
            sessionStartedAtMs={session.sessionStartedAtMs}
            sessionDurationMs={session.sessionDurationMs}
            bucketWindowMs={session.bucketWindowMs}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Events ({session.events.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {session.events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events recorded.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time (ms from start)</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {session.events.map((e, i) => (
                  <TableRow key={`${e.atMs}-${i}`}>
                    <TableCell className="font-mono text-xs">
                      {Math.round(e.atMs - session.sessionStartedAtMs)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{e.kind}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {e.actionKind ?? formatEventDetail(e)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function formatEventDetail(e: { deltaMb?: number | null }): string {
  if (e.deltaMb != null) {
    return `+${e.deltaMb} MB`;
  }
  return "";
}
